import { GenericOperator } from "enums/genericOperator";
import { EligibilityRule, GenericQuestionGroup, PossibleAnswer, StepConstraint } from "../../types/interfaces";
import { GenericProfilingState } from "../genericProfilingSlice";
import { SurveyDirection } from "../types/enums";

export const debugConstraints = (message: string, data: any) => {
  // if (process.env.NODE_ENV === 'development') {
  //   console.log(`[CONSTRAINTS DEBUG] ${message}:`, data);
  // }
};

/**
 * Réponses pré-remplies d'un questionnaire fraîchement chargé, d'après la valeur par défaut
 * portée par chaque question : le booléen prend sa valeur, le choix prend la réponse possible
 * dont la clé correspond (même forme que la réponse cliquée par l'utilisateur). Les réponses
 * déjà enregistrées, rechargées ensuite, écrasent ces valeurs.
 */
export const defaultAnswersOf = (questionsGroups: GenericQuestionGroup[]): Record<string, PossibleAnswer[]> => {
  const answers: Record<string, PossibleAnswer[]> = {};
  questionsGroups.forEach((group) => {
    group.questions.forEach((question) => {
      if (question.defaultValue === undefined || !question.key) return;
      if (question.type === "boolean") {
        answers[question.key] = [question.defaultValue === "true"];
        return;
      }
      const possibleAnswer = question.possibleAnswers.find(
        (answer) => typeof answer === "object" && "key" in answer && answer.key === question.defaultValue
      );
      if (possibleAnswer !== undefined) {
        answers[question.key] = [possibleAnswer];
      }
    });
  });
  return answers;
};

export const updateVisibleSteps = (state: GenericProfilingState) => {
  const previousVisible = state.visibleSteps.map(s => s.step);

  debugConstraints('Answers avant calcul des steps visibles', state.answers);

  state.visibleSteps = getVisibleSteps(
    state.profilingConfig.questionsGroups,
    state.answers,
    state.profilingConfig.constraintsEnabled
  );

  const newVisible = state.visibleSteps.map(s => s.step);

  debugConstraints('Steps visibles', {
    previous: previousVisible,
    new: newVisible,
    constraintsEnabled: state.profilingConfig.constraintsEnabled
  });
};

export const findNextValidStep = (state: GenericProfilingState, direction: SurveyDirection): number | null => {
  const currentIndex = state.visibleSteps.findIndex(s => s.step === state.currentStep);

  debugConstraints('Navigation', {
    currentStep: state.currentStep,
    currentIndex,
    direction,
    visibleSteps: state.visibleSteps.map(s => s.step)
  });

  if (currentIndex === -1) {
    return state.visibleSteps.length > 0 ? state.visibleSteps[0].step : null;
  }

  if (direction === SurveyDirection.Next) {
    const nextStep = state.visibleSteps[currentIndex + 1];
    return nextStep ? nextStep.step : null;
  } else {
    const previousStep = state.visibleSteps[currentIndex - 1];
    return previousStep ? previousStep.step : null;
  }
};

export const ensureValidCurrentStep = (state: GenericProfilingState) => {
  const isCurrentStepVisible = state.visibleSteps.some(s => s.step === state.currentStep);

  if (!isCurrentStepVisible && state.visibleSteps.length > 0) {
    // Le step courant n'est plus visible, aller au premier step visible
    const firstVisibleStep = state.visibleSteps[0].step;
    debugConstraints('Step courant non visible, redirection', {
      from: state.currentStep,
      to: firstVisibleStep
    });
    state.currentStep = firstVisibleStep;
  }
};


export const updateCurrentStepInfos = (state: GenericProfilingState) => {
  const currentGroup = state.visibleSteps.find(g => g.step === state.currentStep);
  if (currentGroup) {
    state.currentStepInfos = {
      title: currentGroup.title,
      iconKey: currentGroup.iconKey
    };
  } else {
    state.currentStepInfos = undefined;
  }
};

export const updateEligibility = (state: GenericProfilingState) => {
  if (state.profilingConfig.eligibilityRules && state.profilingConfig.constraintsEnabled) {
    state.eligibilityResult = checkEligibility(state.answers, state.profilingConfig.eligibilityRules);
  }
};

export const evaluateConstraint = (constraint: StepConstraint, answers: Record<string, PossibleAnswer[]>): boolean => {
  const answerValue = answers[constraint.questionKey];

  if (answerValue === undefined || answerValue === null) {
    return false;
  }

  // Extraire la valeur réelle
  let actualValue;

  if (Array.isArray(answerValue)) {
    const firstAnswer = answerValue[0];

    // Si c'est un objet complexe avec une propriété 'key'
    if (typeof firstAnswer === 'object' && firstAnswer !== null && 'key' in firstAnswer) {
      actualValue = firstAnswer.key;
    } else {
      actualValue = firstAnswer;
    }
  } else {
    actualValue = answerValue;
  }

  let result = false;

  switch (constraint.operator) {
    case GenericOperator.Equals:
      result = actualValue === constraint.value;
      break;
    case GenericOperator.NotEquals:
      result = actualValue !== constraint.value;
      break;
    case GenericOperator.In:
      result = constraint.values?.includes(actualValue) ?? false;
      break;
    case GenericOperator.NotIn:
      result = !(constraint.values?.includes(actualValue) ?? false);
      break;
    default:
      result = false;
  }

  return result;
};

export const evaluateConstraints = (constraints: StepConstraint[], answers: Record<string, PossibleAnswer[]>): boolean => {
  // BUG IDENTIFIÉ : Si constraints est undefined ou vide, retourner true
  if (!constraints || constraints.length === 0) {
    return true; // Pas de contraintes = toujours satisfait
  }

  // Toutes les contraintes doivent être satisfaites (AND logique)
  return constraints.every(constraint => evaluateConstraint(constraint, answers));
};

export const shouldShowStep = (step: GenericQuestionGroup, answers: Record<string, any>): boolean => {
  debugConstraints(`Évaluation step ${step.step} - ${step.title}`, {
    step: step.step,
    title: step.title,
    constraints: step.constraints,
    skipIf: step.skipIf,
    answers
  });

  // Vérifier les contraintes d'affichage
  const constraintsMet = evaluateConstraints(step.constraints || [], answers);
  debugConstraints(`Contraintes d'affichage pour step ${step.step}`, {
    constraints: step.constraints,
    constraintsDefined: !!step.constraints,
    constraintsLength: step.constraints?.length || 0,
    met: constraintsMet
  });

  if (!constraintsMet) {
    debugConstraints(`Step ${step.step} caché par contraintes d'affichage`, {});
    return false;
  }

  // Vérifier les contraintes de saut - BUG ICI !
  const shouldSkip = step.skipIf ? evaluateConstraints(step.skipIf, answers) : false;
  debugConstraints(`Contraintes de saut pour step ${step.step}`, {
    skipIf: step.skipIf,
    skipIfDefined: !!step.skipIf,
    skipIfLength: step.skipIf?.length || 0,
    shouldSkip
  });

  if (shouldSkip) {
    debugConstraints(`Step ${step.step} sauté par contraintes skipIf`, {});
    return false;
  }

  debugConstraints(`Step ${step.step} visible`, {});
  return true;
};

export const getVisibleSteps = (
  questionsGroups: GenericQuestionGroup[],
  answers: Record<string, any>,
  constraintsEnabled: boolean = false
): GenericQuestionGroup[] => {
  debugConstraints('Calcul des steps visibles', {
    totalSteps: questionsGroups.length,
    constraintsEnabled,
    answers
  });

  if (!constraintsEnabled) {
    debugConstraints('Contraintes désactivées - tous les steps visibles', {});
    return questionsGroups;
  }

  const visibleSteps = questionsGroups.filter(step => shouldShowStep(step, answers));

  debugConstraints('Résultat steps visibles', {
    visible: visibleSteps.map(s => ({ step: s.step, title: s.title })),
    hidden: questionsGroups.filter(s => !visibleSteps.includes(s)).map(s => ({ step: s.step, title: s.title }))
  });

  return visibleSteps;
};


export const checkEligibility = (
  answers: Record<string, PossibleAnswer[]>,
  eligibilityRules: EligibilityRule[] = []
): { isEligible: boolean; rule?: EligibilityRule } => {
  for (const rule of eligibilityRules) {
    if (evaluateConstraints(rule.constraints, answers)) {
      return {
        isEligible: rule.result === 'eligible',
        rule
      };
    }
  }

  return { isEligible: true };
};

export const getQuestionEligibilityMessage = (
  questionKey: string,
  questionValue: any,
  eligibilityRules: EligibilityRule[] = []
): string | undefined => {
  for (const rule of eligibilityRules) {
    if (rule.result === 'non-eligible') {
      // Vérifier si cette règle concerne la question spécifique
      const concernsQuestion = rule.constraints.some(constraint =>
        constraint.questionKey === questionKey
      );

      if (concernsQuestion) {
        // Simuler les réponses pour cette question uniquement
        const mockAnswers: Record<string, PossibleAnswer[]> = {
          [questionKey]: Array.isArray(questionValue) ? questionValue : [questionValue]
        };

        // Vérifier si cette combinaison déclenche la règle
        if (evaluateConstraints(rule.constraints, mockAnswers)) {
          return rule.message || rule.reason || "Cette réponse vous rend inéligible";
        }
      }
    }
  }

  return undefined;
};

export const getAllEligibilityMessagesForQuestion = (
  questionKey: string,
  possibleAnswers: PossibleAnswer[],
  eligibilityRules: EligibilityRule[] = []
): Record<string, string> => {
  const eligibilityMessages: Record<string, string> = {};

  possibleAnswers.forEach(answer => {
    let answerKey: string;

    // Extraire la clé de la réponse
    if (typeof answer === 'object' && answer !== null && 'key' in answer) {
      answerKey = String(answer.key);
    } else {
      answerKey = String(answer);
    }

    const message = getQuestionEligibilityMessage(questionKey, answer, eligibilityRules);
    if (message) {
      eligibilityMessages[answerKey] = message;
    }
  });

  return eligibilityMessages;
};
